<template>
	<div class="main">
		<div v-if="!isLoading">
			<div class="text-left main flex justify-between flex-wrap flex-col md:flex-row mb-4">
				<div class="mr-8 w-48 flex-grow mb-4 md:mb-0">
					<p v-if="isAllowedToUpload">
						{{ $t('working_photos_description_app_container') }}
					</p>
					<p v-else>
						{{ $t('working_photos_description_no_app_container') }}
					</p>
				</div>

				<div class="flex justify-center w-full" v-if="!workingImages">
					<LoadingSpinnerComponent />
				</div>

				<div class="flex w-full flex-wrap mt-4 justify-end">
					<div class="inline-flex">
						<div class="mr-8">
							<BaseButton @click="toggleCheckboxes(true)" v-if="workingImages && workingImages.length" class="mr-2">
								<font-awesome-icon
									:class="{ 'text-green-300 opacity-100': true }"
									icon="check"
									class="text-gray-300"
									style="font-size: 20px"
								></font-awesome-icon>
								Alle
							</BaseButton>
							<BaseButton @click="toggleCheckboxes(false)" v-if="workingImages && workingImages.length">
								<font-awesome-icon
									:class="{ 'text-green-300 opacity-100': false }"
									icon="check"
									class="text-gray-300"
									style="font-size: 20px"
								></font-awesome-icon>
								{{ $t('none') }}
							</BaseButton>
						</div>
						<div :class="hasCustomerAppConnection ? 'mr-2' : ''" v-if="!isAppContainer">
							<BaseButton class="self-end mr-4 w-full text-center" @click="getDownloadURL" :disabled="!enableDownloadImageFiles">{{
								selectedImagesCounter === 1
									? $t('download_photo', { count: selectedImagesCounter })
									: $t('download_photos', { count: selectedImagesCounter })
							}}</BaseButton>
						</div>
						<div v-if="hasCustomerAppConnection" :class="workingImages.length === 0 ? 'mr-8' : ''">
							<BaseButton class="self-end mr-4 w-full text-center" @click="unblockInCustomerApp" :disabled="!enableDownloadImageFiles">{{
								selectedImagesCounter === 1
									? $t('unblock_image_in_customer_app', { count: selectedImagesCounter })
									: $t('unblock_images_in_customer_app', { count: selectedImagesCounter })
							}}</BaseButton>
						</div>
					</div>
					<div class="ml-8 mr-8 text-right" v-if="workingImages && workingImages.length && getAuthenticatedUserGroupLevel !== '2'">
						<div class="flex">
							<BaseButton :disabled="!enableGeneratePdf" @click="createPDF" class="w-full mr-2">
								{{ $t('generate_pdf') }}
							</BaseButton>
							<BaseButton :disabled="!pdfDownloadURL" @click="downloadFile">
								<font-awesome-icon icon="download" style="font-size: 20px"></font-awesome-icon>
							</BaseButton>
						</div>
						<div class="mt-2">
							<BaseCheckbox label="Datum & Uhrzeit in PDF" v-model="printTime" :filter-only="true" />
						</div>
					</div>

					<div class="mr-8 mb-4 md:mb-0 text-right" v-if="isAllowedToUpload">
						<BaseCamera
							:label="$t('store_working_photo')"
							class="inline-flex"
							:disable-success-message="true"
							:enable-preview="false"
							:allow-upload-in-app-container="false"
							@snapshot="snapshot"
						/>
					</div>
				</div>
			</div>

			<div class="flex w-full flex-wrap">
				<div class="w-1/3 pr-8 pb-8" v-for="(workingImage, key) in workingImages" :key="workingImage.id">
					<MetaImage
						:meta-info-media="workingImage"
						:editable="getAuthenticatedUserGroupLevel !== '2'"
						:chooseable="true"
						v-model="workingImage.isSelected"
						@delete="deleteImage(key)"
						@input="checkIsFileSelected"
						:hide-show-customer="true"
						:ref="workingImage.id"
						:deletable="getAuthenticatedUserGroupLevel !== '2'"
					></MetaImage>
				</div>
			</div>
		</div>
		<LoadingSpinnerWidget v-else />
	</div>
</template>

<script>
import { CustomersApiFactory, DamagecasesApiFactory, MetainfofileApiFactory, PdfApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import MetaImage from '@/components/damagecases/forms/MetaInfoImage';
import BaseButton from '@/components/forms/buttons/BaseButton';
import BaseCheckbox from '@/components/forms/inputs/BaseCheckbox';
import BaseCamera from '@/components/forms/inputs/BaseCamera';
import { v4 } from 'uuid';
import { MetaInfoFileTypeEnum, PDFTypes } from '@/api/generated/models';
import LoadingSpinner from '@/mixins/LoadingSpinner';
import LoadingSpinnerComponent from '@/components/navigation/LoadingSpinner';
import AppContainerMixin from '@/mixins/AppContainerMixin';
import Authentication from '@/mixins/Authentication';
import { default as LoadingSpinnerWidget } from '@/components/navigation/LoadingSpinner.vue';
import { getRegularDate } from '@/plugins/Utils';
import DamageCaseRepository from '@/api/repositories/DamageCaseRepository';
import PDFDamagecasesRepository from '@/api/repositories/PDFDamagecasesRepository';
import PDFRepository from '@/api/repositories/PDFRepository';

export default {
	name: 'WorkingImages',
	components: { LoadingSpinnerWidget, LoadingSpinnerComponent, BaseCamera, BaseCheckbox, BaseButton, MetaImage },
	mixins: [LoadingSpinner, AppContainerMixin, Authentication],
	data() {
		return {
			toggleButtonText: this.$t('select_all_photos'),
			enableDownloadImageFiles: false,
			enableGeneratePdf: false,
			latestUuid: null,
			workingImages: null,
			printTime: false,
			checkboxSelectedStatus: false,
			selectedImagesCounter: 0,
			pdfDownloadURL: '',
			documentLabel: 'Arbeitsfotos',
			isLoading: true,
			hasCustomerAppConnection: false,
		};
	},
	created: function() {
		CustomersApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
			.customersIdGet(this.$route.params.customer)
			.then(response => {
				this.hasCustomerAppConnection = response.data.customer_main != null;
				this.$store.commit('setCustomer', response.data);
			});
		this.refIndex = 0;
	},
	computed: {
		isAllowedToUpload() {
			return this.isUserAllowedInLevel5or6 || (!this.isUserAllowedInLevel2 && !this.isUserAllowedInLevel5or6);
		},
	},
	methods: {
		getChildRef() {
			this.refIndex++;
			return 'metaImageChild' + this.refIndex;
		},
		toggleCheckboxes(status) {
			const array = this.workingImages;

			for (let i = 0; i < array.length; i++) {
				const file = array[i];

				this.$refs[file.id][0].toggleCheckMetaInfoImage(status);
			}
		},
		checkIsFileSelected() {
			this.enableDownloadImageFiles = undefined;
			this.enableDownloadImageFiles = this.isOneFileSelected(this.workingImages);
			this.enableGeneratePdf = undefined;
			this.enableGeneratePdf = this.isOneFileSelected(this.workingImages);

			if (this.workingImages) {
				let counter = 0;
				for (let i = 0; i < this.workingImages.length; i++) {
					if (this.workingImages[i].isSelected !== undefined && this.workingImages[i].isSelected !== false) {
						counter++;
					}
				}
				this.selectedImagesCounter = counter;
			}

			if (!this.enableDownloadImageFiles) {
				this.checkboxSelectedStatus = false;
				this.toggleButtonText = this.$t('select_all_photos');
			}
		},
		isOneFileSelected(files) {
			return files.find(element => element.isSelected) !== undefined;
		},
		deleteImage: function(index) {
			MetainfofileApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.metainfofileIdDelete(this.workingImages[index].id, 'workingImages')
				.then(result => {
					this.workingImages.splice(index, 1);
					this.$swal(this.$t('general.success').toString(), this.$t('photo_deleted').toString(), 'success');
				})
				.catch(error => {
					this.$swal(this.$t('general.error').toString(), this.$t('photo_delete_error').toString(), 'error');
				});
		},
		snapshot: function($image) {
			this.workingImages = this.workingImages ?? [];
			this.workingImages.unshift({
				id: v4(),
				timestamp: '-',
				type: MetaInfoFileTypeEnum.Image,
				base64Content: $image,
			});

			if (this.hasCustomerAppConnection) {
				this.$swal({
					title: this.$t('general.attention').toString(),
					text: this.$t('make_available_in_app').toString(),
					cancelButtonText: this.$t('general.no').toString(),
					showCancelButton: true,
					confirmButtonText: this.$t('general.yes').toString(),
				})
					.then(result => {
						this.workingImages[0].show_customer = !!result.value;
					})
					.then(_ => {
						this.uploadImage();
					});
			} else {
				this.uploadImage();
			}
		},
		uploadImage() {
			DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.damagecasesIdImageTypePost(parseInt(this.$route.params.processId || this.$route.params.id), 'workingimages', this.workingImages[0])
				.then(result => {
					this.workingImages[0]['id'] = result.data.id;
					this.workingImages[0]['timestamp'] = result.data.date_of_capture;
					this.$store.commit('clearDamageCaseWorkingImages');
					this.updateImages();
					this.$swal(this.$t('general.success').toString(), this.$t('photo_uploaded').toString(), 'success');
				})
				.catch(error => {
					this.$swal(this.$t('general.error').toString(), this.$t('photo_upload_error').toString(), 'error');
				});
		},
		createPDF: function() {
			this.setLoadingStatus(true);
			this.enableGeneratePdf = false;
			this.pdfDownloadURL = '';

			const ids = [];
			const array = this.workingImages;

			for (let i = 0; i < array.length; i++) {
				const file = array[i];

				if (file.id && file.isSelected) {
					ids.push(file.id);
				}
			}

			DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.damagecasesIdGet(parseInt(this.$route.params.processId || this.$route.params.id))
				.then(response => {
					response.data.imageDocumentData = { documentTitle: this.$t('work_images'), printTime: this.printTime, mediaIds: ids };
					PdfApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
						.pdfDamagecasesTypeSavePost(PDFTypes.Workingimages, response.data)
						.then(res => {
							this.latestUuid = res.data.uuid;

							this.enableGeneratePdf = true;
							this.$swal(this.$t('general.success').toString(), this.$t('pdf_generated').toString(), 'success');
							new PDFDamagecasesRepository().getSavedDocument(this.latestUuid).then(response => {
								this.documentLabel = response.headers['content-disposition'].split('filename=')[1];
								this.pdfDownloadURL = new Blob([response.data], { type: 'application/pdf' });
							});
							this.setLoadingStatus(false);
						})
						.catch(error => {
							this.$swal(this.$t('general.error').toString(), this.$t('error_generating_document').toString(), 'error');
							this.enableGeneratePdf = true;
							this.setLoadingStatus(false);
						});
				})
				.catch(error => {
					this.setLoadingStatus(false);
				});
		},
		downloadFile() {
			if (this.isAppContainer) {
				const reader = new FileReader();
				const name = this.documentLabel;
				reader.onload = function() {
					const arrayBuffer = this.result;
					// convert the ArrayBuffer to a comma-separated string
					const dataString = new Uint8Array(arrayBuffer).join(',');
					const messageData = {
						fileName: name,
						data: dataString,
					};
					// send the data to Swift
					window.webkit.messageHandlers.pdfHandler.postMessage(JSON.stringify(messageData));
				};
				reader.readAsArrayBuffer(this.pdfDownloadURL);
			} else {
				const url = window.URL.createObjectURL(this.pdfDownloadURL);
				const name = this.documentLabel;
				setTimeout(function() {
					const a = document.createElement('a');
					a.style.display = 'none';
					a.href = url;
					a.download = name;
					document.body.appendChild(a);
					a.click();
					window.URL.revokeObjectURL(url);
				}, 200);
			}
		},
		loadImage(workingImages) {
			DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.damagecasesIdImageTypeGet(parseInt(this.$route.params.processId || this.$route.params.id), 'workingimages')
				.then(result => {
					workingImages.imageFiles = result.data;
				})
				.catch(error => {});
		},
		checkIfPdfDocumentExists() {
			new PDFRepository()
				.getPDFFile(parseInt(this.$route.params.processId || this.$route.params.id), 'workingimages')
				.then(result => {
					this.isLoading = false;
					if (result.data.data.uuid) {
						this.latestUuid = result.data.data.uuid;
						new PDFDamagecasesRepository().getSavedDocument(this.latestUuid).then(response => {
							this.documentLabel = response.headers['content-disposition'].split('filename=')[1];
							this.pdfDownloadURL = new Blob([response.data], { type: 'application/pdf' });
						});
					}
				})
				.catch(error => {
					this.isLoading = false;
				});
		},
		unblockInCustomerApp() {
			const images = [];
			this.workingImages.forEach(image => {
				if (image.isSelected) {
					images.push(image.working_image_id);
				}
			});
			new DamageCaseRepository()
				.unblockWorkingImagesInCustomerApp(this.$route.params.processId || this.$route.params.id, images)
				.then(response => {
					this.$swal(this.$t('general.success').toString(), this.$t('successfully_unblocked_images_in_app').toString(), 'success').then(_ => {
						this.updateImages();
					});
				})
				.catch(error => {
					this.$swal(this.$t('general.error').toString(), this.$t('error_unblocking_images').toString(), 'error');
				});
		},
		getDownloadURL() {
			const ids = [];
			const array = this.workingImages;

			for (let i = 0; i < array.length; i++) {
				const file = array[i];

				if (file.id && file.isSelected) {
					ids.push(file.id);
				}
			}

			let url = '';
			new DamageCaseRepository().downloadWorkingImages(parseInt(this.$route.params.processId), { mediaIds: ids }).then(response => {
				const fileName = response.headers['content-disposition'].split('filename=')[1];
				const blob = new Blob([response.data]);
				url = window.URL.createObjectURL(blob);
				setTimeout(function() {
					const a = document.createElement('a');
					a.style.display = 'none';
					a.href = url;
					a.download = fileName;
					document.body.appendChild(a);
					a.click();
					window.URL.revokeObjectURL(url);
				}, 200);
			});
		},
		updateImages() {
			DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.damagecasesIdImageTypeGet(parseInt(this.$route.params.processId || this.$route.params.id), 'workingimages')
				.then(result => {
					this.workingImages = result.data;

					if (this.workingImages.length) {
						this.workingImages.forEach(workingImage => {
							this.loadImage(workingImage);
						});
					}
				})
				.catch(error => {
					this.workingImages = null;
				});
		},
	},
	mounted() {
		this.isLoading = true;
		this.updateImages();
		this.checkIfPdfDocumentExists();
	},
};
</script>

<style scoped></style>
